/**
 * Audit Logging Type Definitions
 * HIPAA-compliant audit logging for PHI access tracking
 *
 * HIPAA Requirements:
 * - 6-year retention minimum (2555 days)
 * - Track all PHI access, creation, modification, deletion
 * - Include user identity, timestamp, action, resource
 * - Log successful and failed access attempts
 * - Maintain audit trail integrity (tamper-proof)
 */

// Import PHI classification from canonical source
import { PHIClassification } from './phi.types';

// Re-export PHIClassification for consumers of this module
export { PHIClassification };

/**
 * Audit log severity levels
 */
export enum AuditLogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

/**
 * PHI-specific action types requiring audit logging
 */
export enum PHIAccessAction {
  // Read operations
  VIEW = 'view',
  SEARCH = 'search',
  EXPORT = 'export',

  // Write operations
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',

  // Encryption operations
  ENCRYPT = 'encrypt',
  DECRYPT = 'decrypt',
  DECRYPT_FAILED = 'decrypt_failed',
  PRINT = 'print',

  // Sharing operations
  SHARE = 'share',
  REVOKE_ACCESS = 'revoke_access',
}

/**
 * Resource types that can be audited
 */
export enum AuditResourceType {
  PATIENT_PROFILE = 'patient_profile',
  PATIENT_SUMMARY = 'patient_summary',
  PATIENT_CONTACT = 'patient_contact',
  PATIENT_MEDICAL_RECORD = 'patient_medical_record',
  CLINICAL_NOTE = 'clinical_note',
  ENCOUNTER = 'encounter',
  PRESCRIPTION = 'prescription',
  LAB_RESULT = 'lab_result',
  INSURANCE = 'insurance',
  APPOINTMENT = 'appointment',
  DOCUMENT = 'document',
  AUDIT_LOG = 'audit_log',
}

/**
 * Complete audit log entry
 * All fields required for HIPAA compliance
 */
export interface AuditLogEntry {
  /** Unique identifier for the audit log entry */
  id: string;

  /** Timestamp of the event (ISO 8601 format) */
  timestamp: Date;

  /** User ID who performed the action */
  userId: string;

  /** Optional username for human readability */
  username?: string;

  /** Role of the user at time of action */
  userRole?: string;

  /** Action performed */
  action: PHIAccessAction;

  /** Type of resource accessed */
  resourceType: AuditResourceType;

  /** ID of the specific resource (e.g., patient ID) */
  resourceId: string;

  /** PHI classification level (if applicable) */
  classification?: PHIClassification;

  /** Whether the action was successful */
  success: boolean;

  /** Error message if action failed */
  errorMessage?: string;

  /** Severity level of the audit event */
  level: AuditLogLevel;

  /** IP address of the client */
  ipAddress?: string;

  /** User agent string (browser/device info) */
  userAgent?: string;

  /** Session ID for correlation */
  sessionId?: string;

  /** Additional context data (non-PHI) */
  metadata?: Record<string, any>;

  /** Hash of previous audit log entry for tamper detection */
  previousHash?: string;

  /** Hash of this entry for integrity verification */
  entryHash?: string;
}

/**
 * PHI access event (simplified for logging)
 */
export interface PHIAccessEvent {
  userId: string;
  action: PHIAccessAction;
  resourceType: AuditResourceType;
  resourceId: string;
  classification?: PHIClassification;
  success: boolean;
  errorMessage?: string;
  metadata?: Record<string, any>;
}

/**
 * Audit log query filters
 */
export interface AuditLogFilters {
  /** Filter by user ID */
  userId?: string;

  /** Filter by action type */
  action?: PHIAccessAction;

  /** Filter by resource type */
  resourceType?: AuditResourceType;

  /** Filter by resource ID */
  resourceId?: string;

  /** Filter by success/failure */
  success?: boolean;

  /** Filter by date range (start) */
  startDate?: Date;

  /** Filter by date range (end) */
  endDate?: Date;

  /** Filter by severity level */
  level?: AuditLogLevel;

  /** Pagination limit */
  limit?: number;

  /** Pagination offset */
  offset?: number;
}

/**
 * Audit log statistics
 */
export interface AuditLogStats {
  /** Total number of audit log entries */
  totalEntries: number;

  /** Number of failed access attempts */
  failedAttempts: number;

  /** Number of PHI access events */
  phiAccessCount: number;

  /** Most accessed resource type */
  topResourceType?: AuditResourceType;

  /** Most common action */
  topAction?: PHIAccessAction;

  /** Date range of statistics */
  dateRange: {
    start: Date;
    end: Date;
  };
}

/**
 * Audit log retention configuration
 */
export interface AuditRetentionConfig {
  /** Retention period in days (HIPAA requires minimum 2555 days / 6 years) */
  retentionDays: number;

  /** Whether to archive old logs before deletion */
  archiveBeforeDelete: boolean;

  /** Archive storage location */
  archiveLocation?: string;

  /** Whether to encrypt archived logs */
  encryptArchive: boolean;
}

/**
 * Audit log batch for performance optimization
 */
export interface AuditLogBatch {
  /** Batch ID for tracking */
  batchId: string;

  /** Entries in this batch */
  entries: AuditLogEntry[];

  /** Batch timestamp */
  timestamp: Date;

  /** Batch size */
  size: number;
}
